import React from 'react';
import { ArrowLeft, FileText, ShieldCheck, Lock, CreditCard, AlertCircle, RefreshCw, Edit3 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function TermsConditions() {
  const navigate = useNavigate();

  return (
    <div className="bg-[#fcfcfc] min-h-screen py-6 sm:py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Header Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-xs mb-6 sm:mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-xs sm:text-sm font-bold text-gray-500 hover:text-red-600 transition mb-4 cursor-pointer"
          >
            <ArrowLeft size={16} /> Back
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100">
              <FileText size={24} />
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-extrabold text-gray-900">Terms & Conditions</h1>
              <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                Payment Gateway & Booking Terms for Lokanatya.com
              </p>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-xs space-y-8">
          
          {/* Section 1 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-600 text-xs font-black flex items-center justify-center">1</span>
              Introduction
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed font-normal">
              When you book tickets through <strong>Lokanatya.com</strong>, all transactions are processed securely through a third-party payment gateway. By using our services, you agree to the terms outlined in this section regarding payment processing.
            </p>
          </div>

          {/* Section 2 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-600 text-xs font-black flex items-center justify-center">2</span>
              Payment Processing
            </h2>
            <div className="space-y-4 text-xs sm:text-sm text-gray-600 leading-relaxed">
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                  <CreditCard size={16} className="text-red-500" /> Payment Gateway Provider
                </h3>
                <p>
                  Lokanatya.com utilizes <strong>Razorpay</strong> to handle all payment transactions. This provider is responsible for securely processing your credit card, debit card, UPI, or other payment options.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-1">Payment Information Required</h3>
                <p>
                  You will be required to provide payment details, including but not limited to your card number, expiration date, CVV, or UPI details. This information is collected and processed directly by Razorpay, and we do not store your full payment information on our servers.
                </p>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-600 text-xs font-black flex items-center justify-center">3</span>
              Security & Storage
            </h2>
            <div className="space-y-3 text-xs sm:text-sm text-gray-600 leading-relaxed">
              <p>
                <strong>Encryption and Protection:</strong> Razorpay uses industry-standard encryption technology (such as SSL/TLS) to protect your payment information during transmission. We are committed to ensuring the security of your payment details through our provider's secure system.
              </p>
              <p>
                <strong>No Storage of Payment Details:</strong> We do not store your card details on our systems. All payment information is processed in real time and handled in accordance with Razorpay's privacy and security practices.
              </p>
            </div>
          </div>

          {/* Section 4 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-600 text-xs font-black flex items-center justify-center">4</span>
              Payment Confirmation
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              Once your payment is successfully processed, you will receive a confirmation message and your e-ticket with QR code under <strong>My Tickets</strong>. If you do not receive this confirmation within a reasonable timeframe, please contact our customer support team for assistance.
            </p>
          </div>

          {/* Section 5 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-600 text-xs font-black flex items-center justify-center">5</span>
              Errors and Disputes
            </h2>
            <div className="space-y-3 text-xs sm:text-sm text-gray-600 leading-relaxed">
              <p>
                <strong>Transaction Errors:</strong> If you believe there is an error with your transaction or double-deduction, please contact our customer support team immediately. We will work with Razorpay to investigate and resolve any issues as quickly as possible.
              </p>
              <p>
                <strong>Disputes:</strong> Any disputes related to payment transactions should be directed to our customer support team. We will assist in resolving disputes with Razorpay as necessary. However, Lokanatya.com is not responsible for errors originating solely from user bank servers.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div className="border-b border-gray-100 pb-6">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-600 text-xs font-black flex items-center justify-center">6</span>
              Refunds and Cancellations
            </h2>
            <div className="space-y-3 text-xs sm:text-sm text-gray-600 leading-relaxed">
              <p>
                <strong>Refund Policy:</strong> Refunds are processed according to our official Refund Policy. Approved refunds are handled by Razorpay in accordance with their standard processing timeline (typically 5-7 business days).
              </p>
              <p>
                <strong>Cancellations:</strong> If you need to cancel a booking or if a show is cancelled by the event organizers, please refer to our Cancellation Policy for details on eligibility and procedures.
              </p>
            </div>
          </div>

          {/* Section 7 */}
          <div>
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-red-100 text-red-600 text-xs font-black flex items-center justify-center">7</span>
              Changes to Payment Terms
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              We reserve the right to update these Payment Gateway Terms and Conditions at any time. Any changes will become effective immediately upon posting on our website. It is your responsibility to review these terms periodically for updates.
            </p>
          </div>

        </div>

      </div>
    </div>
  );
}
